import React, { Component } from "react"
import { connect } from "react-redux"
import { Link } from "react-router-dom"
import { deleteUsers, getUsers } from "./redux/reducers"
import { Arwes, Button, Frame as FrameC, Loading, Words } from "arwes"
import Icon from "@mdi/react"
import { mdiChemicalWeapon, mdiRefresh } from "@mdi/js"
import get from "lodash.get"
import styled from "styled-components"
import UserAvatar from "../../common/UserAvatar"
import { formatPostDate } from "../../common/util/dates"
import { isAdminUser } from "../../common/util/isAdmin"
import { MarginTop } from "../../common/styled/MarginTop"
import {
  ContentColumn,
  PageBlock,
  PageOffset
} from "../../common/styled/Page"

const PER_PAGE = 12
const MAX_BATCH_DELETE = 10

const PageHeading = styled.h3`
  display: flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px 18px;
  margin: 10px 8px;
  text-align: center;
`

const HeadingMeta = styled.span`
  font-size: 0.7em;
  color: #ffb800;
  font-family: "VT323", monospace;
  letter-spacing: 0.1em;
`

const SubHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin: 0 0 20px;
  font-family: "VT323", monospace;
  font-size: 1.1rem;
  color: #6fbfff;
`

// Roster rows: avatar rail + identity + action, forum-style.
const RosterRow = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: ${p =>
    p.adminView ? "44px 72px minmax(0, 1fr) auto" : "72px minmax(0, 1fr) auto"};
  align-items: center;
  gap: 6px;
  padding: 12px 16px 12px 0;

  &:not(:last-child) {
    border-bottom: 1px dashed rgba(190, 38, 252, 0.18);
  }

  &:hover {
    background: rgba(0, 231, 255, 0.04);
  }
`

const SelectionCell = styled.label`
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  cursor: ${p => (p.disabled ? "not-allowed" : "pointer")};

  input {
    width: 20px;
    height: 20px;
    margin: 0;
    accent-color: #fc26fa;
    cursor: inherit;
  }
`

const RowRail = styled.div`
  display: flex;
  justify-content: center;
`

const Identity = styled.div`
  min-width: 0;
`

const OperatorName = styled(Link)`
  font-family: "VT323", monospace;
  font-size: 1.15rem;
  color: #00e7ff;
  text-decoration: none;
  word-break: break-word;

  &:hover {
    color: #17ff0e;
  }

  @media (max-width: 600px) {
    &::after {
      content: "";
      position: absolute;
      inset: 0;
      z-index: 1;
    }

    &:focus::after {
      outline: 2px solid #00e7ff;
      outline-offset: -2px;
    }
  }
`

const MetaLine = styled.div`
  font-family: "VT323", monospace;
  font-size: 0.85rem;
  margin-top: 3px;

  .msgs {
    color: #ffb800;
    letter-spacing: 0.08em;
  }
  .since {
    color: #3a4670;
  }
  .email {
    color: #6fbfff;
    overflow-wrap: anywhere;
  }
  .protected {
    color: #fc26fa;
    letter-spacing: 0.08em;
  }
`

const RowAction = styled.div`
  flex-shrink: 0;

  @media (max-width: 600px) {
    display: none;
  }
`

const PagingBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 20px;
  font-family: "VT323", monospace;
  color: #6fbfff;
  letter-spacing: 0.08em;
`

const AdminToolbar = styled.div`
  margin-bottom: 14px;
  padding: 12px;
  background: rgba(252, 38, 250, 0.05);
  border: 1px solid rgba(252, 38, 250, 0.35);
  font-family: "VT323", monospace;
`

const AdminToolbarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
`

const AdminActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`

const SelectionCount = styled.span`
  color: #ffb800;
  letter-spacing: 0.08em;
`

const BatchMessage = styled.div`
  margin-top: 10px;
  color: ${p => (p.error ? "#fc26fa" : "#17ff0e")};
  letter-spacing: 0.04em;
`

const ConfirmationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed rgba(252, 38, 250, 0.35);
  color: #fc26fa;
`

class Users extends Component {
  state = {
    show: false,
    selectedIds: [],
    isDeleting: false,
    confirmBatchDelete: false,
    batchMessage: "",
    batchError: ""
  }

  componentDidMount() {
    // Admins always refresh so rows loaded anonymously cannot hide
    // protected/admin status or private email fields.
    if (
      this.props.isAdmin ||
      !Array.isArray(get(this.props.loadedUsers, "users"))
    ) {
      const page = get(this.props.loadedUsers, "page", 1)
      this.props.getUsers(page, PER_PAGE)
    }
    setTimeout(() => this.setState({ show: true }), 600)
  }

  componentDidUpdate(previousProps) {
    if (!previousProps.isAdmin && this.props.isAdmin) {
      const page = get(this.props.loadedUsers, "page", 1)
      this.props.getUsers(page, PER_PAGE)
    }
  }

  refresh = () => {
    const page = get(this.props.loadedUsers, "page", 1)
    this.setState({
      selectedIds: [],
      confirmBatchDelete: false,
      batchMessage: "",
      batchError: ""
    })
    this.props.getUsers(page, PER_PAGE)
  }

  changePage = page => {
    this.setState({
      selectedIds: [],
      confirmBatchDelete: false,
      batchMessage: "",
      batchError: ""
    })
    this.props.getUsers(page, PER_PAGE)
    window.scrollTo(0, 0)
  }

  toggleSelection = userId => {
    this.setState(state => {
      const selected = state.selectedIds.includes(userId)
      if (selected) {
        return {
          selectedIds: state.selectedIds.filter(id => id !== userId),
          confirmBatchDelete: false,
          batchMessage: "",
          batchError: ""
        }
      }
      if (state.selectedIds.length >= MAX_BATCH_DELETE) return null
      return {
        selectedIds: [...state.selectedIds, userId],
        confirmBatchDelete: false,
        batchMessage: "",
        batchError: ""
      }
    })
  }

  clearSelection = () => {
    this.setState({
      selectedIds: [],
      confirmBatchDelete: false,
      batchError: ""
    })
  }

  handleBatchDelete = async () => {
    const selectedIds = [...this.state.selectedIds]
    if (!selectedIds.length || selectedIds.length > MAX_BATCH_DELETE) return

    const currentPage = get(this.props.loadedUsers, "page", 1)
    const currentUsers = get(this.props.loadedUsers, "users", [])
    const remainingOnPage = Array.isArray(currentUsers)
      ? currentUsers.filter(user => !selectedIds.includes(user._id)).length
      : 0

    this.setState({
      isDeleting: true,
      confirmBatchDelete: false,
      batchMessage: "",
      batchError: ""
    })

    const result = await this.props.deleteUsers(selectedIds)
    if (!result || !result.ok) {
      this.setState({
        selectedIds: [],
        isDeleting: false,
        batchError: (result && result.error) || "Unable to delete users"
      })
      this.props.getUsers(currentPage, PER_PAGE)
      return
    }

    const nextPage = currentPage > 1 && remainingOnPage === 0
      ? currentPage - 1
      : currentPage
    this.props.getUsers(nextPage, PER_PAGE)
    this.setState({
      selectedIds: [],
      isDeleting: false,
      batchMessage: `${result.deletedCount} user${
        result.deletedCount === 1 ? "" : "s"
      } deleted`,
      batchError: ""
    })
  }

  renderRow = user => {
    const { isAdmin, viewerId } = this.props
    const selected = this.state.selectedIds.includes(user._id)
    const protectedUser =
      !!user.isProtected || user._id === viewerId || !!user.isAdmin
    const selectionLimitReached =
      this.state.selectedIds.length >= MAX_BATCH_DELETE
    const disabledByLimit =
      !protectedUser && !selected && selectionLimitReached
    const selectionDisabled =
      protectedUser || disabledByLimit || this.state.isDeleting
    const protectedLabel = user._id === viewerId
      ? "current administrator"
      : user.isProtected
        ? "guest user"
        : user.isAdmin
          ? "administrator"
          : ""

    return (
      <RosterRow key={user._id} adminView={isAdmin}>
        {isAdmin && (
          <SelectionCell disabled={selectionDisabled}>
            <input
              type="checkbox"
              checked={selected}
              disabled={selectionDisabled}
              aria-label={
                protectedUser
                  ? `${user.name} is protected from batch deletion`
                  : disabledByLimit
                    ? `Selection limit of ${MAX_BATCH_DELETE} reached; deselect a user before selecting ${user.name}`
                  : `Select ${user.name}${user.email ? ` (${user.email})` : ""}`
              }
              title={
                disabledByLimit
                  ? `Selection limit of ${MAX_BATCH_DELETE} reached`
                  : undefined
              }
              onChange={() => this.toggleSelection(user._id)}
            />
          </SelectionCell>
        )}
        <RowRail>
          <UserAvatar user={user} size={56} square />
        </RowRail>
        <Identity>
          <OperatorName to={`/user/${user._id}`}>{user.name}</OperatorName>
          <MetaLine>
            <span className="msgs">MSGS: {user.messageCount || 0}</span>{" "}
            <span className="since">
              · since {formatPostDate(user.created)}
            </span>
            {isAdmin && user.email && (
              <React.Fragment>
                <br />
                <span className="email">{user.email}</span>
              </React.Fragment>
            )}
            {isAdmin && protectedLabel && (
              <React.Fragment>
                {" "}
                <span className="protected">· {protectedLabel}</span>
              </React.Fragment>
            )}
          </MetaLine>
        </Identity>
        <RowAction>
          <Button
            animate
            layer="control"
            onClick={() => this.props.history.push(`/user/${user._id}`)}
          >
            <Icon path={mdiChemicalWeapon} size={0.5} color="#00e7ff" spin />{" "}
            View
          </Button>
        </RowAction>
      </RosterRow>
    )
  }

  render() {
    const { loadedUsers, isExpanded, isAdmin } = this.props
    const users = get(loadedUsers, "users")
    const page = get(loadedUsers, "page", 1)
    const total = get(loadedUsers, "total", 0)
    const hasMore = get(loadedUsers, "hasMore", false)
    const pages = Math.max(Math.ceil(total / PER_PAGE), 1)
    const isReady = Array.isArray(users)

    return (
      <Arwes>
        <PageOffset isExpanded={isExpanded}>
          <MarginTop />
          <ContentColumn maxWidth={760}>
            <PageBlock>
              <FrameC
                show={this.state.show}
                animate
                level={2}
                corners={4}
                layer="alert"
              >
                <PageHeading>
                  <Words animate layer="alert">
                    OPERATORS
                  </Words>
                  <HeadingMeta>{total} REGISTERED</HeadingMeta>
                </PageHeading>
              </FrameC>
            </PageBlock>

            <PageBlock>
              <SubHead>
                <span>&gt; the network's registered operators · newest first</span>
                <Button animate layer="control" onClick={this.refresh}>
                  <Icon path={mdiRefresh} size={0.6} color="#00e7ff" /> Refresh
                </Button>
              </SubHead>

              {isReady && isAdmin && (
                <AdminToolbar
                  role="region"
                  aria-label="Admin batch user deletion"
                >
                  <AdminToolbarRow>
                    <span>ADMIN // SELECT UP TO {MAX_BATCH_DELETE} USERS</span>
                    <AdminActions>
                      <SelectionCount aria-live="polite">
                        {this.state.selectedIds.length} / {MAX_BATCH_DELETE} SELECTED
                      </SelectionCount>
                      <Button
                        animate
                        layer="control"
                        disabled={
                          !this.state.selectedIds.length || this.state.isDeleting
                        }
                        onClick={this.clearSelection}
                      >
                        CLEAR
                      </Button>
                      <Button
                        animate
                        layer="alert"
                        disabled={
                          !this.state.selectedIds.length || this.state.isDeleting
                        }
                        onClick={() =>
                          this.setState({ confirmBatchDelete: true })
                        }
                      >
                        {this.state.isDeleting
                          ? "DELETING…"
                          : "DELETE SELECTED"}
                      </Button>
                    </AdminActions>
                  </AdminToolbarRow>

                  {this.state.confirmBatchDelete && (
                    <ConfirmationRow role="alert">
                      <span>
                        Delete {this.state.selectedIds.length} selected user
                        {this.state.selectedIds.length === 1 ? "" : "s"}?
                      </span>
                      <AdminActions>
                        <Button
                          animate
                          layer="alert"
                          onClick={this.handleBatchDelete}
                        >
                          CONFIRM DELETE
                        </Button>
                        <Button
                          animate
                          layer="success"
                          onClick={() =>
                            this.setState({ confirmBatchDelete: false })
                          }
                        >
                          CANCEL
                        </Button>
                      </AdminActions>
                    </ConfirmationRow>
                  )}

                  {this.state.batchMessage && (
                    <BatchMessage role="status">
                      {this.state.batchMessage}
                    </BatchMessage>
                  )}
                  {this.state.batchError && (
                    <BatchMessage error role="alert">
                      {this.state.batchError}
                    </BatchMessage>
                  )}
                </AdminToolbar>
              )}

              {!isReady && (
                <div style={{ margin: "30px", textAlign: "center" }}>
                  <Loading animate small />
                </div>
              )}

              {isReady && (
                <FrameC
                  show={this.state.show}
                  animate
                  level={3}
                  corners={3}
                  layer="primary"
                >
                  <div style={{ padding: "4px 0" }}>
                    {users.map(this.renderRow)}
                  </div>
                </FrameC>
              )}

              {isReady && (
                <PagingBar>
                  <Button
                    animate
                    layer="success"
                    disabled={page <= 1}
                    onClick={() => this.changePage(page - 1)}
                  >
                    ◂ PREV
                  </Button>
                  <span>
                    PAGE {page} / {pages} · {total} OPERATORS
                  </span>
                  <Button
                    animate
                    layer="success"
                    disabled={!hasMore}
                    onClick={() => this.changePage(page + 1)}
                  >
                    NEXT ▸
                  </Button>
                </PagingBar>
              )}
            </PageBlock>
          </ContentColumn>
        </PageOffset>
      </Arwes>
    )
  }
}

const mapState = state => {
  return {
    loadedUsers: state.user.loadedUsers,
    isAdmin: isAdminUser(state),
    viewerId: get(state, "user.loaded.user._id")
  }
}

export default connect(
  mapState,
  { deleteUsers, getUsers }
)(Users)
